import { useState, useRef } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';
import './ImageUploader.css';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_SIZE = 5 * 1024 * 1024; // 5MB

export function ImageUploader({ postId, onUploadComplete, label = 'Upload Image' }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Please upload a JPG, PNG, WebP, or GIF image';
    }
    if (file.size > MAX_SIZE) {
      return 'File size must be less than 5MB';
    }
    return null;
  };

  const uploadFile = async (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setUploading(true);
    setProgress(0);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    // Generate unique filename
    const timestamp = Date.now();
    const filename = `${timestamp}-${file.name}`;
    const storageRef = ref(storage, `posts/${postId || 'temp'}/${filename}`);

    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const prog = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(Math.round(prog));
      },
      (err) => {
        console.error('Upload error:', err);
        setError('Failed to upload image');
        setUploading(false);
        setPreview(null);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setUploading(false);
          onUploadComplete(downloadURL);
        } catch (err) {
          console.error('Error getting download URL:', err);
          setError('Failed to get image URL');
          setUploading(false);
        }
      }
    );
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const clearPreview = () => {
    setPreview(null);
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="image-uploader">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept={ACCEPTED_TYPES.join(',')}
        className="image-uploader__input"
      />

      {preview ? (
        <div className="image-uploader__preview">
          <img src={preview} alt="Preview" />
          {uploading && (
            <div className="image-uploader__progress">
              <div
                className="image-uploader__progress-bar"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
          {!uploading && (
            <button
              type="button"
              onClick={clearPreview}
              className="image-uploader__remove btn btn-ghost"
            >
              Remove
            </button>
          )}
        </div>
      ) : (
        <div
          className={`image-uploader__dropzone ${isDragging ? 'image-uploader__dropzone--active' : ''}`}
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <span className="image-uploader__icon">+</span>
          <span className="text-sans text-small text-muted">{label}</span>
          <span className="text-sans text-small text-muted">
            or drag and drop
          </span>
        </div>
      )}

      {error && <p className="image-uploader__error text-sans text-small">{error}</p>}
    </div>
  );
}
